<template>
  <v-container fluid>
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-3">{{ $t('refund.refundLoading') }}</div>
    </div>

    <div v-else-if="loadError" class="text-center py-12">
      <v-icon large color="error">mdi-alert-circle-outline</v-icon>
      <div class="mt-3">{{ $t('refund.refundLoadingError') }}</div>
      <v-btn class="mt-3" color="primary" outlined @click="get">{{ $t('refund.retry') }}</v-btn>
    </div>

    <div v-else-if="!refund" class="text-center py-12">
      <v-icon large color="grey">mdi-file-search-outline</v-icon>
      <div class="mt-3">{{ $t('refund.notFound') }}</div>
    </div>

    <template v-else>
      <refund-header :refund="refund" />

      <refund-articles-fees
        :items="refund.items || []"
        :fees="refund.fees || []"
        :currency="refund.currency"
        :amount-with-tax="refund.amount"
        :amount-without-tax="refund.amount_exclude_tax"
      />

      <refund-transactions
        :transactions="refund.transactions || []"
        :currency="refund.currency"
        :can-update="canChangeStatus"
        :updating="updating"
        @forceSuccess="onForceSuccess"
      />

      <refund-status-menu
        v-if="canChangeStatus && hasStatusTransitions"
        :current-status="refund.current_status"
        :updating="updating"
        @changeStatus="onChangeStatus"
      />
    </template>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'
  import { EventBus } from '../../event-bus'
  import { getAllowedRefundStatusTransitions } from '../../helpers/refundHelper'
  import { RefundService } from '../../services/refundService'
  import { ReturnSettingsService } from '@/services/returnSettingsService'
  import RefundHeader from '@/components/refunds/RefundHeader.vue'
  import RefundArticlesFees from '@/components/refunds/RefundArticlesFees.vue'
  import RefundTransactions from '@/components/refunds/RefundTransactions.vue'
  import RefundStatusMenu from '@/components/refunds/RefundStatusMenu.vue'

  export default {
    name: 'RefundDetail',
    components: {
      RefundHeader,
      RefundArticlesFees,
      RefundTransactions,
      RefundStatusMenu,
    },
    data() {
      return {
        refund: null,
        loading: false,
        loadError: false,
        updating: false,
        refundService: {},
        returnSettingsService: null,
        returnSettings: {},
      }
    },
    created() {
      this.refundService = new RefundService(this.$auth)
      this.returnSettingsService = new ReturnSettingsService(this.$auth)
    },
    mounted() {
      this.get()
      this.getReturnSettings()
    },
    methods: {
      async get() {
        this.loading = true
        this.loadError = false
        try {
          const res = await this.refundService.get(this.$route.params.id)
          // null = genuine 404 (not found); a thrown error = transient failure (see catch).
          this.refund = res ?? null
        } catch {
          this.loadError = true
          this.refund = null
        } finally {
          this.loading = false
        }
      },
      /**
       * Loads the returns settings carrying permissions.refund_update_roles, the per-tenant
       * allowlist that gates the "change status" action. Secure-by-default: a failed fetch
       * leaves an empty object, which denies access for non-super-admin users.
       */
      async getReturnSettings() {
        try {
          const countryIso3 = this.currentStoreDetails?.address?.country_id
          this.returnSettings = (await this.returnSettingsService.getReturnSettings(countryIso3)) || {}
        } catch (e) {
          console.error(e)
          this.returnSettings = {}
        }
      },
      /**
       * Persists a refund status change via PATCH /v1/int/refunds/{id}, then refreshes
       * the local view from the API response. On failure the service surfaces the error
       * and rethrows, so the UI is left untouched.
       *
       * @param {string} status - The new refund status.
       */
      async onChangeStatus(status) {
        // Guard against a second concurrent change while the first PATCH is in flight.
        if (this.updating) {
          return
        }
        this.updating = true
        try {
          const updated = await this.refundService.update(this.$route.params.id, { current_status: status })
          this.refund = updated ?? { ...this.refund, current_status: status }
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('refund.statusUpdated'),
            color: 'success',
          })
        } catch {
          // Error already surfaced by the service; keep the current view unchanged.
        } finally {
          this.updating = false
        }
      },
      /**
       * Forces a transaction to the success status from the row context menu, persisting it
       * via PATCH (transactions are matched by transaction_id), then refreshes from the response.
       * On failure the service surfaces the error and rethrows, so the UI is left untouched.
       *
       * @param {Object} transaction - The transaction to force to success.
       */
      async onForceSuccess(transaction) {
        // Guard against a second concurrent force while the first PATCH is in flight.
        if (this.updating) {
          return
        }
        this.updating = true
        try {
          const forced = { ...transaction, current_status: 'success' }
          const updated = await this.refundService.update(this.$route.params.id, { transactions: [forced] })
          if (updated) {
            this.refund = updated
          } else {
            const transactions = (this.refund.transactions || []).map((tx) =>
              tx.transaction_id && tx.transaction_id === transaction.transaction_id
                ? { ...tx, current_status: 'success' }
                : tx
            )
            this.refund = { ...this.refund, transactions }
          }
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('refund.transactionForcedSuccess'),
            color: 'success',
          })
        } catch {
          // Error already surfaced by the service; keep the current view unchanged.
        } finally {
          this.updating = false
        }
      },
    },
    computed: {
      ...mapState(['roles', 'isOctipasAdmin', 'currentStoreDetails']),
      /**
       * Whether the current refund status allows any transition. When it doesn't (terminal
       * `refunded` refund), the change-status button is hidden entirely rather than opening a
       * menu where every option is disabled.
       *
       * @returns {boolean}
       */
      hasStatusTransitions() {
        return getAllowedRefundStatusTransitions(this.refund?.current_status).length > 0
      },
      /**
       * Gates the "change status" button on permissions.refund_update_roles from the returns
       * settings: the current user needs at least one role present in that allowlist.
       * Super admin (octipas-admin) always has access, regardless of the setting.
       * Secure-by-default: an empty/absent allowlist denies access to everyone else.
       */
      canChangeStatus() {
        if (this.isOctipasAdmin) {
          return true
        }
        const allowedRoles = this.returnSettings?.permissions?.refund_update_roles
        if (Array.isArray(allowedRoles) && Array.isArray(this.roles)) {
          return this.roles.some((role) => allowedRoles.includes(role))
        }
        return false
      },
    },
  }
</script>
